import React from 'react';
import { Text, Section, Link } from '@react-email/components';
import Layout, { LayoutTranslations } from './components/Layout';

export interface LevelChangedTranslations {
  title: string;
  greeting: string;
  bodyUpgrade: string;
  bodyDowngrade: string;
  labelOldLevel: string;
  labelNewLevel: string;
  newFeatures: string;
  ctaText: string;
  signOff: string;
  teamSignature: string;
  levelFree: string;
  levelPremium: string;
  levelVip: string;
  layout: LayoutTranslations;
}

interface LevelChangedProps {
  name: string;
  oldLevel: string;
  newLevel: string;
  translations: LevelChangedTranslations;
}

const LEVEL_ORDER: Record<string, number> = { free: 0, premium: 1, vip: 2 };

export default function LevelChanged({
  name,
  oldLevel,
  newLevel,
  translations: tx,
}: LevelChangedProps) {
  const isUpgrade = (LEVEL_ORDER[newLevel] ?? 0) > (LEVEL_ORDER[oldLevel] ?? 0);
  const body = isUpgrade ? tx.bodyUpgrade : tx.bodyDowngrade;

  const getLevelLabel = (level: string) => {
    if (level === 'vip') return tx.levelVip;
    if (level === 'premium') return tx.levelPremium;
    return tx.levelFree;
  };

  const newLevelColor = newLevel === 'vip' ? '#d97706' : newLevel === 'premium' ? '#2563eb' : '#6b7280';
  const accentColor = isUpgrade ? '#059669' : '#6b7280';

  return (
    <Layout previewText={tx.title} translations={tx.layout}>
      <Text style={{ ...heading, color: accentColor }}>{tx.title}</Text>
      <Text style={paragraph}>{tx.greeting} <strong>{name}</strong>,</Text>
      <Text style={paragraph}>{body}</Text>

      <Section style={infoBox}>
        <Text style={infoLine}>
          <span style={infoLabel}>{tx.labelOldLevel}:</span> {getLevelLabel(oldLevel)}
        </Text>
        <Text style={infoLine}>
          <span style={infoLabel}>{tx.labelNewLevel}:</span>{' '}
          <strong style={{ color: newLevelColor }}>{getLevelLabel(newLevel)}</strong>
        </Text>
      </Section>

      {isUpgrade && (
        <Text style={paragraph}>{tx.newFeatures}</Text>
      )}

      <Section style={ctaContainer}>
        <Link href="https://siportevent.com/visitor/dashboard" style={{ ...ctaButton, backgroundColor: accentColor }}>
          {tx.ctaText}
        </Link>
      </Section>

      <Text style={paragraph}>{tx.signOff}<br />{tx.teamSignature}</Text>
    </Layout>
  );
}

const heading: React.CSSProperties = { fontSize: '24px', fontWeight: 700, margin: '0 0 20px 0' };
const paragraph: React.CSSProperties = { fontSize: '16px', lineHeight: '1.6', color: '#374151', margin: '0 0 16px 0' };
const infoBox: React.CSSProperties = { backgroundColor: '#eff6ff', padding: '20px', borderRadius: '8px', margin: '20px 0', border: '1px solid #bfdbfe' };
const infoLine: React.CSSProperties = { fontSize: '15px', color: '#1f2937', margin: '0 0 10px 0' };
const infoLabel: React.CSSProperties = { color: '#6b7280' };
const ctaContainer: React.CSSProperties = { textAlign: 'center' as const, margin: '30px 0' };
const ctaButton: React.CSSProperties = { display: 'inline-block', padding: '14px 28px', color: '#ffffff', textDecoration: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '16px' };
